/**
 * 详细评估API
 * 基于RTK Query的详细评估数据管理
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './baseApi';
import type {
  DetailedAssessment,
  AssessmentModule,
  AssessmentReport,
  ModuleName,
  CreateAssessmentRequest,
  UpdateModuleRequest,
  SubmitAssessmentRequest,
  GenerateReportRequest,
  AssessmentResponse,
  ModuleResponse,
  ReportResponse,
} from '../../types/detailed-assessment';

export const detailedAssessmentApi = createApi({
  reducerPath: 'detailedAssessmentApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['DetailedAssessment', 'AssessmentModule', 'AssessmentReport'],

  endpoints: builder => ({
    // 获取详细评估数据
    getDetailedAssessment: builder.query<
      DetailedAssessment,
      {
        projectId: string;
        include?: 'modules' | 'reports' | 'tasks' | 'attachments' | 'all';
      }
    >({
      query: ({ projectId, include }) => ({
        url: `/projects/${projectId}/detailed-assessment`,
        method: 'GET',
        params: include ? { include } : undefined,
      }),
      transformResponse: (response: AssessmentResponse) => response.data!,
      providesTags: (result, error, { projectId }) => [
        { type: 'DetailedAssessment', id: projectId },
        { type: 'DetailedAssessment', id: 'LIST' },
      ],
    }),

    // 获取特定模块数据
    getAssessmentModule: builder.query<
      AssessmentModule,
      {
        projectId: string;
        moduleName: ModuleName;
      }
    >({
      query: ({ projectId, moduleName }) => ({
        url: `/projects/${projectId}/detailed-assessment/modules/${moduleName}`,
        method: 'GET',
      }),
      transformResponse: (response: ModuleResponse) => response.data!,
      providesTags: (result, error, { projectId, moduleName }) => [
        { type: 'AssessmentModule', id: `${projectId}-${moduleName}` },
      ],
    }),

    // 更新模块数据
    updateAssessmentModule: builder.mutation<
      AssessmentModule,
      {
        projectId: string;
        moduleName: ModuleName;
        data: UpdateModuleRequest;
      }
    >({
      query: ({ projectId, moduleName, data }) => ({
        url: `/projects/${projectId}/detailed-assessment/modules/${moduleName}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ModuleResponse) => response.data!,
      invalidatesTags: (result, error, { projectId, moduleName }) => [
        { type: 'AssessmentModule', id: `${projectId}-${moduleName}` },
        { type: 'DetailedAssessment', id: projectId },
        { type: 'DetailedAssessment', id: 'LIST' },
      ],
    }),

    // 批量更新模块数据
    batchUpdateModules: builder.mutation<
      DetailedAssessment,
      {
        projectId: string;
        modules: Array<{
          moduleName: ModuleName;
          data: UpdateModuleRequest;
        }>;
      }
    >({
      queryFn: async ({ projectId, modules }, api, extraOptions, baseQuery) => {
        const results = [];

        // 逐个更新模块
        for (const module of modules) {
          const result = await baseQuery({
            url: `/projects/${projectId}/detailed-assessment/modules/${module.moduleName}`,
            method: 'PUT',
            body: module.data,
          });

          if (result.error) {
            return { error: result.error };
          }

          results.push(result.data);
        }

        // 获取更新后的完整评估数据
        const assessmentResult = await baseQuery({
          url: `/projects/${projectId}/detailed-assessment`,
          method: 'GET',
          params: { include: 'modules' },
        });

        if (assessmentResult.error) {
          return { error: assessmentResult.error };
        }

        return { data: (assessmentResult.data as AssessmentResponse).data! };
      },
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DetailedAssessment', id: projectId },
        { type: 'DetailedAssessment', id: 'LIST' },
        { type: 'AssessmentModule', id: `${projectId}-ALL` },
      ],
    }),

    // 提交详细评估
    submitDetailedAssessment: builder.mutation<
      DetailedAssessment,
      {
        projectId: string;
        data?: SubmitAssessmentRequest;
      }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/detailed-assessment/submit`,
        method: 'POST',
        body: data || {},
      }),
      transformResponse: (response: AssessmentResponse) => response.data!,
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'DetailedAssessment', id: projectId },
        { type: 'DetailedAssessment', id: 'LIST' },
      ],
    }),

    // 生成评估报告
    generateAssessmentReport: builder.mutation<
      AssessmentReport,
      {
        projectId: string;
        data: GenerateReportRequest;
      }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/detailed-assessment/generate-report`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ReportResponse) => response.data!,
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'AssessmentReport', id: `${projectId}-LIST` },
      ],
    }),

    // 获取评估报告列表
    getAssessmentReports: builder.query<
      AssessmentReport[],
      {
        projectId: string;
        reportType?: string;
      }
    >({
      query: ({ projectId, reportType }) => ({
        url: `/projects/${projectId}/detailed-assessment/reports`,
        method: 'GET',
        params: reportType ? { type: reportType } : undefined,
      }),
      transformResponse: (response: {
        success: boolean;
        data: AssessmentReport[];
      }) => response.data,
      providesTags: (result, error, { projectId }) => [
        { type: 'AssessmentReport', id: `${projectId}-LIST` },
        ...(result || []).map(report => ({
          type: 'AssessmentReport' as const,
          id: report.id,
        })),
      ],
    }),

    // 保存草稿数据
    saveDraftModule: builder.mutation<
      AssessmentModule,
      {
        projectId: string;
        moduleName: ModuleName;
        data: Partial<UpdateModuleRequest>;
      }
    >({
      query: ({ projectId, moduleName, data }) => ({
        url: `/projects/${projectId}/detailed-assessment/modules/${moduleName}/draft`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ModuleResponse) => response.data!,
      // 草稿保存不需要立即更新缓存，避免过于频繁的更新
      invalidatesTags: [],
    }),

    // 验证评估完整性
    validateAssessment: builder.query<
      {
        isComplete: boolean;
        errors: string[];
        missingModules: string[];
        completionPercentage: number;
      },
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/detailed-assessment/validate`,
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: any }) =>
        response.data,
    }),

    // 获取评估历史记录
    getAssessmentHistory: builder.query<
      Array<{
        id: string;
        action: string;
        module_name?: string;
        change_summary: string;
        performed_at: string;
        performed_by: string;
      }>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/detailed-assessment/history`,
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: any[] }) =>
        response.data,
    }),

    // 导出评估数据
    exportAssessmentData: builder.mutation<
      {
        download_url: string;
        file_name: string;
      },
      {
        projectId: string;
        format: 'excel' | 'pdf' | 'json';
        includeReports?: boolean;
      }
    >({
      query: ({ projectId, format, includeReports }) => ({
        url: `/projects/${projectId}/detailed-assessment/export`,
        method: 'POST',
        body: { format, include_reports: includeReports },
      }),
      transformResponse: (response: { success: boolean; data: any }) =>
        response.data,
    }),

    // 获取评估模板
    getAssessmentTemplate: builder.query<
      {
        modules: Record<string, any>;
        questions: Record<string, any>;
        validation_rules: Record<string, any>;
      },
      { templateType?: string }
    >({
      query: ({ templateType = 'detailed_assessment' }) => ({
        url: `/assessment-templates/${templateType}`,
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: any }) =>
        response.data,
    }),
  }),
});

// 导出hooks
export const {
  useGetDetailedAssessmentQuery,
  useGetAssessmentModuleQuery,
  useUpdateAssessmentModuleMutation,
  useBatchUpdateModulesMutation,
  useSubmitDetailedAssessmentMutation,
  useGenerateAssessmentReportMutation,
  useGetAssessmentReportsQuery,
  useSaveDraftModuleMutation,
  useLazyValidateAssessmentQuery,
  useGetAssessmentHistoryQuery,
  useExportAssessmentDataMutation,
  useGetAssessmentTemplateQuery,
} = detailedAssessmentApi;

// 导出API reducer
export default detailedAssessmentApi;
